import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import Button from '../components/ui/Button'
import KpiTile from '../components/ui/KpiTile'
import AppShell from '../components/ui/AppShell'
import BottomNav from '../components/ui/BottomNav'
import { useSettings } from '../api/settings'
import { getUser, logout } from '../api/auth'
import { useIsMobile } from '../hooks/useIsMobile'
import { useLiveStream } from '../hooks/useLiveStream'
import { getFleetLive, getTruckHistory, getTruckLedger, getRefuelSchedule, getAlertCounts } from '../api/tankguard'
import { getGPSTrack } from '../utils/odometer'
import FleetMap from '../components/FleetMap'
import { ImpersonationBanner, AnnouncementBanner } from '../components/AppBanners'
import PushPrompt from '../components/PushPrompt'
import InstallPrompt from '../components/InstallPrompt'
import TruckList from '../components/TruckList'
import EventFeed from '../components/EventFeed'
import TruckDetail from '../components/TruckDetail'
import { KpiSkeleton } from '../components/Skeleton'
import Trucks from './Trucks'
import Reports from './Reports'
import Settings from './Settings'
import Geofences from './Geofences'
import RouteHistory from './RouteHistory'
import SensorHealth from './SensorHealth'
import Alerts from './Alerts'

export default function Dashboard({ onLogout }) {
  const settings = useSettings()
  const { t } = useTranslation()
  const DIESEL = settings.diesel
  const user = getUser()
  const [trucks, setTrucks] = useState([])
  const [events, setEvents] = useState([])
  const [selectedHistory, setSelectedHistory] = useState({})
  const [selectedLedger, setSelectedLedger] = useState(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [fetchErr, setFetchErr] = useState('')
  const [errDismissed, setErrDismissed] = useState(false)
  const [selected, setSelected] = useState(null)
  const [tab, setTab] = useState('dashboard')
  const [lastUpdated, setLastUpdated] = useState(null)
  const [secondsAgo, setSecondsAgo] = useState(0)
  const [refuelSchedule, setRefuelSchedule] = useState(null)
  const [refuelOpen, setRefuelOpen] = useState(true)
  // Persisted alert-workflow roll-up (alert_notifications) — distinct from the
  // transient fleet `events` feed. Drives the nav badge + the theft KPI.
  const [alertCounts, setAlertCounts] = useState({ open: 0, acknowledged: 0, active_theft: 0 })
  // Alert id to deep-link into (from a push tap) — handed to <Alerts>.
  const [deepLinkAlertId, setDeepLinkAlertId] = useState(null)

  const loadAlertCounts = useCallback(() => {
    getAlertCounts()
      .then(setAlertCounts)
      .catch(err => { console.error('Alert counts fetch failed:', err) })
  }, [])

  // Shared payload → state mapping, used by both the HTTP poll and the SSE
  // stream (both deliver the same /fleet/live payload shape).
  const applyFleet = useCallback((payload) => {
    if (!payload) return
    setTrucks(payload.trucks.map(t => ({ ...t, hasAlert: t.has_alert, lastSeen: t.last_seen })))
    // Merge fuel events + geofence events into one feed, newest first. Theft
    // alerts stay distinguishable by `type` so EventFeed picks the right icon.
    const merged = [...(payload.events || []), ...(payload.geofence_events || [])]
      .sort((a, b) => b.ts - a.ts)
    setEvents(merged)
    setLastUpdated(payload.updated_at)
    setSecondsAgo(0)
    setFetchErr('')
  }, [])

  const load = useCallback(async () => {
    try {
      applyFleet(await getFleetLive())
    } catch (e) {
      console.error(e); Sentry.captureException(e)
      setFetchErr(e.message)
      setErrDismissed(false)
    }
    setLoading(false)
  }, [applyFleet])

  // Live server push (SSE). `fleet` frames carry the same payload the poll
  // returns; an `alert` frame means something new fired, so refresh the feed
  // immediately instead of waiting for the next fleet frame.
  const { connected: live } = useLiveStream({
    onFleet: applyFleet,
    onAlert: () => { load(); loadAlertCounts() },
    onAlertUpdate: () => { loadAlertCounts() },
  })

  // Deep-link into a specific alert after a push tap. Two entry points:
  //   (1) ?alert=<id> in the URL — cold open / notificationclick openWindow
  //   (2) a postMessage from the service worker — app already open + focused
  // Either one switches to the alerts tab and tells <Alerts> which row to open.
  useEffect(() => {
    function openAlert(id) {
      if (!id) return
      setDeepLinkAlertId(String(id))
      setTab('alerts')
    }
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get('alert')
    if (fromUrl) {
      openAlert(fromUrl)
      params.delete('alert') // strip so a refresh doesn't reopen it
      const qs = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''))
    }
    function onSwMessage(e) {
      if (e.data && e.data.type === 'notification-click') openAlert(e.data.alertId)
    }
    if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', onSwMessage)
    return () => {
      if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', onSwMessage)
    }
  }, [])

  // Initial load always; the poll loop only runs while the stream is down —
  // when `live` flips, the effect re-runs and swaps mode automatically.
  useEffect(() => {
    load()
    loadAlertCounts()
    if (live) return undefined
    const t = setInterval(() => { load(); loadAlertCounts() }, settings.refreshMs)
    return () => clearInterval(t)
  }, [load, loadAlertCounts, settings.refreshMs, live])

  useEffect(() => {
    let cancelled = false
    const fetchSchedule = () => {
      getRefuelSchedule()
        .then(data => { if (!cancelled) setRefuelSchedule(data) })
        .catch(err => { console.error('Refuel schedule fetch failed:', err); Sentry.captureException(err) })
    }
    fetchSchedule()
    const t = setInterval(fetchSchedule, Math.max(settings.refreshMs, 30000))
    return () => { cancelled = true; clearInterval(t) }
  }, [settings.refreshMs])
  useEffect(() => {
    const t = setInterval(() => setSecondsAgo(s => s + 1), 1000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (!selected) { setSelectedHistory({}); setSelectedLedger(null); setHistoryLoading(false); return }
    let cancelled = false
    setHistoryLoading(true)
    setSelectedHistory({})
    setSelectedLedger(null)
    // History + ledger are independent fetches — kick them off in parallel so
    // the chart and the ledger table fill at the same time. Either failing
    // does not block the other; we just leave the corresponding panel empty.
    Promise.allSettled([
      getTruckHistory(selected),
      getTruckLedger(selected, 24),
    ]).then(([histR, ledgerR]) => {
      if (cancelled) return
      if (histR.status === 'fulfilled' && histR.value) setSelectedHistory(histR.value.history || {})
      else if (histR.status === 'rejected') { console.error('Truck history fetch failed:', histR.reason); Sentry.captureException(histR.reason) }
      if (ledgerR.status === 'fulfilled' && ledgerR.value) setSelectedLedger(ledgerR.value)
      else if (ledgerR.status === 'rejected') { console.error('Truck ledger fetch failed:', ledgerR.reason); Sentry.captureException(ledgerR.reason) }
      setHistoryLoading(false)
    })
    return () => { cancelled = true }
  }, [selected])

  const totalLiters = trucks.reduce((s, t) => s + (t.fuel_liters || 0), 0)
  const totalEur = Math.round(totalLiters * DIESEL)
  const online = trucks.filter(t => t.online).length
  const tampers = events.filter(e => e.type === 'tamper').length
  // Cumulative litres Tankguard has flagged as theft across the fleet's lifetime,
  // valued at today's diesel price — the "theft prevented" value narrative that
  // the weekly report leads with. Active theft = open, unresolved theft/tamper
  // alerts (from the workflow roll-up), i.e. what needs action right now.
  const theftPreventedL = trucks.reduce((s, t) => s + (Number(t.lifetime_theft_l) || 0), 0)
  const theftPreventedEur = Math.round(theftPreventedL * DIESEL)

  const selectedTruck = trucks.find(t => t.id === selected)
  const gpsTrack = selected ? getGPSTrack(selectedHistory) : []

  // KPI row reframed around the security value story, not just litres:
  // €-protected · theft-prevented · active-theft · fleet-online.
  const activeTheft = alertCounts.active_theft
  const kpiTiles = [
    { label: t('dashboard.underProtection'), value: '€' + totalEur.toLocaleString(), sub: Math.round(totalLiters) + ' L · ' + trucks.length + ' ' + t('fleet.trucks'), accent: 'teal' },
    { label: t('dashboard.theftPrevented'), value: '€' + theftPreventedEur.toLocaleString(), sub: theftPreventedL > 0 ? Math.round(theftPreventedL) + ' L ' + t('dashboard.caught') : t('dashboard.noTheftDetected'), accent: 'success' },
    { label: t('dashboard.activeTheft'), value: String(activeTheft), sub: activeTheft > 0 ? t('dashboard.requiresAttention') : t('dashboard.noActiveTheft'), variant: activeTheft > 0 ? 'alert' : 'default' },
    { label: t('dashboard.fleetOnline'), value: online + ' / ' + trucks.length, sub: online < trucks.length ? (trucks.length - online) + ' ' + t('dashboard.offline') : t('dashboard.allConnected'), accent: 'success', variant: trucks.length > 0 && online < trucks.length ? 'warning' : 'default' },
  ]

  const mobile = useIsMobile()
  const orgName = user?.org_name || settings.company
  const tabDefs = [
    { key: 'dashboard', label: t('nav.dashboard'), abbr: t('nav.dashboard').slice(0, 4) },
    { key: 'fleet',     label: t('nav.fleet'),     abbr: t('nav.fleet').slice(0, 4) },
    { key: 'alerts',    label: t('nav.alerts'),    abbr: t('nav.alerts').slice(0, 4) },
    { key: 'history',   label: t('nav.history'),   abbr: t('nav.history').slice(0, 4) },
    { key: 'health',    label: t('nav.health'),    abbr: t('nav.health').slice(0, 4) },
    { key: 'geofences', label: t('nav.geofences'), abbr: t('nav.geofences').slice(0, 4) },
    { key: 'reports',   label: t('nav.reports'),   abbr: t('nav.reports').slice(0, 4) },
    { key: 'settings',  label: t('nav.settings'),  abbr: t('nav.settings').slice(0, 4) },
  ]

  return (
    <div className="app-root" style={{ minHeight: '100vh', background: 'var(--tg-bg-page)' }}>
      <ImpersonationBanner />
      <AppShell
        brandName={orgName}
        truckCount={trucks.length || undefined}
        tabs={tabDefs}
        activeTab={tab}
        onTabChange={setTab}
        user={user}
        alertCount={alertCounts.open}
        tamperCount={tampers}
        onAlertClick={() => setTab('alerts')}
        onSignOut={logout}
        secondsAgo={lastUpdated ? secondsAgo : undefined}
        live={live}
        mobile={mobile}
      />

      <AnnouncementBanner />

      <div style={{ padding: '0 var(--tg-space-4)' }}>
        <PushPrompt />
        <InstallPrompt />
      </div>

      {tab === 'fleet'      ? <Trucks />
       : tab === 'alerts'    ? <Alerts onReloadCounts={loadAlertCounts} openAlertId={deepLinkAlertId} onOpened={() => setDeepLinkAlertId(null)} />
       : tab === 'history'   ? <RouteHistory />
       : tab === 'health'    ? <SensorHealth />
       : tab === 'geofences' ? <Geofences />
       : tab === 'reports'   ? <Reports />
       : tab === 'settings'  ? <Settings />
       : (
        <div className={`page${selectedTruck ? ' truck-drawer-open' : ''}`}>
          {/* Active-theft banner — on mobile this is the first thing in view so
              an ongoing theft needs no scrolling to notice. Tapping it jumps to
              the alert workflow. Desktop already surfaces it via the top pill. */}
          {mobile && alertCounts.active_theft > 0 && (
            <button className="mobile-alert-banner" onClick={() => setTab('alerts')}>
              <span className="mobile-alert-banner__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.36 3.59L1.99 17.5A1.91 1.91 0 0 0 3.63 20.4h16.74a1.91 1.91 0 0 0 1.64-2.9L13.64 3.6a1.91 1.91 0 0 0-3.28 0z" /><path d="M12 9v4M12 17h.01" />
                </svg>
              </span>
              <span className="mobile-alert-banner__text">
                <b>{alertCounts.active_theft}</b> {t('dashboard.activeTheft')}
              </span>
              <span className="mobile-alert-banner__cta">{t('dashboard.reviewAlerts')} →</span>
            </button>
          )}
          {fetchErr && !errDismissed && (
            <div style={{ background: 'var(--tg-danger-50)', border: '1px solid var(--tg-danger-100)', borderLeft: '4px solid var(--tg-danger-500)', color: 'var(--tg-danger-700)', fontSize: 'var(--tg-text-sm)', padding: '10px 16px', borderRadius: 'var(--tg-radius-lg)', marginBottom: 'var(--tg-space-4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Couldn't refresh fleet data — showing last known state. {fetchErr}</span>
              <button onClick={() => setErrDismissed(true)} style={{ background: 'none', border: 'none', color: 'var(--tg-danger-700)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>
          )}

          {/* KPI row — show skeletons on the very first load so we never flash
              misleading €0 / 0-of-0 figures before real telemetry arrives. */}
          <div className="kpi-row">
            {loading && trucks.length === 0
              ? [0, 1, 2, 3].map(i => <KpiSkeleton key={i} />)
              : kpiTiles.map(tile => (
                  <KpiTile key={tile.label} label={tile.label} value={tile.value} sub={tile.sub} variant={tile.variant} accent={tile.accent} />
                ))}
          </div>

          {refuelSchedule && refuelSchedule.total > 0 && (
            <div className="tg-card" style={{ marginBottom: 'var(--tg-space-4)', overflow: 'hidden' }}>
              <button
                onClick={() => setRefuelOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                  padding: '12px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 'var(--tg-text-md)', fontWeight: 'var(--tg-weight-semibold)', color: 'var(--tg-text-primary)',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: refuelSchedule.counts.critical > 0 ? 'var(--tg-danger-600)' : 'var(--tg-warning-600, #b45309)' }}>⛽</span>
                  Refuel schedule
                  <span style={{ fontSize: 'var(--tg-text-xs)', fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-muted)' }}>
                    {refuelSchedule.total} truck{refuelSchedule.total === 1 ? '' : 's'} need attention
                  </span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 'var(--tg-text-xs)' }}>
                  {refuelSchedule.counts.critical > 0 && <span style={{ color: 'var(--tg-danger-700)', fontWeight: 600 }}>{refuelSchedule.counts.critical} critical</span>}
                  {refuelSchedule.counts.high > 0 && <span style={{ color: '#92400e', fontWeight: 600 }}>{refuelSchedule.counts.high} high</span>}
                  {refuelSchedule.counts.medium > 0 && <span style={{ color: 'var(--tg-text-muted)' }}>{refuelSchedule.counts.medium} medium</span>}
                  <span style={{ color: 'var(--tg-text-muted)', transform: refuelOpen ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }}>▾</span>
                </span>
              </button>
              {refuelOpen && (
                <div style={{ borderTop: '1px solid var(--tg-border-default)', maxHeight: 220, overflowY: 'auto' }}>
                  {refuelSchedule.trucks.map(t => {
                    const u = t.prediction.urgency
                    const color = u === 'critical' ? 'var(--tg-danger-700)' : u === 'high' ? '#92400e' : 'var(--tg-text-secondary)'
                    return (
                      <div
                        key={t.id}
                        onClick={() => setSelected(t.id)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderBottom: '1px solid var(--tg-border-subtle, #f1f5f9)', cursor: 'pointer', fontSize: 'var(--tg-text-sm)' }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, color: 'var(--tg-text-primary)' }}>{t.name}</span>
                          <span style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-muted)' }}>
                            {Math.round(t.fuel_liters)} L{t.burn_rate_l_h ? ' · burning ' + t.burn_rate_l_h.toFixed(1) + ' L/h' : ''}
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color, fontWeight: 600 }}>
                            Empty in {t.prediction.hours_remaining < 1 ? '<1h' : Math.round(t.prediction.hours_remaining) + 'h'}
                          </div>
                          <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-muted)' }}>
                            {new Date(t.prediction.empty_at).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* empty state */}
          {!loading && trucks.length === 0 && (
            <div className="tg-card" style={{ textAlign: 'center', padding: '50px 20px', marginBottom: 'var(--tg-space-4)' }}>
              <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="var(--tg-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 12px' }}>
                <path d="M7 17a2 2 0 1 0 4 0 2 2 0 1 0-4 0M15 17a2 2 0 1 0 4 0 2 2 0 1 0-4 0M5 17H3V6a1 1 0 0 1 1-1h9v12M13 9h4l3 3v5h-2" />
              </svg>
              <div style={{ fontWeight: 'var(--tg-weight-semibold)', color: 'var(--tg-text-primary)', fontSize: 'var(--tg-text-md)', marginBottom: 6 }}>{t('dashboard.noTrucksInFleet')}</div>
              <div style={{ color: 'var(--tg-text-secondary)', fontSize: 'var(--tg-text-sm)', marginBottom: 20 }}>{t('dashboard.goToFleet')}</div>
              <Button variant="primary" onClick={() => setTab('fleet')}>{t('dashboard.addFirstTruck')}</Button>
            </div>
          )}

          {/* hero map — full-width, the centrepiece of the dashboard */}
          <div className="map-card map-hero">
            <div className="map-head">
              <h2>{t('dashboard.fleetMap')}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--tg-space-4)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--tg-success-500)', display: 'inline-block' }} />{t('dashboard.online')}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--tg-graphite-400)', display: 'inline-block' }} />{t('fleet.offline')}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--tg-danger-500)', display: 'inline-block' }} />{t('dashboard.alert')}
                </span>
              </div>
            </div>
            <div className="map-hero__canvas" style={{ borderTop: '1px solid var(--tg-border-default)', overflow: 'hidden' }}>
              {loading
                ? <div className="tg-skel tg-skel--block" style={{ height: '100%', borderRadius: 0 }} />
                : <FleetMap trucks={trucks} selected={selected} onSelect={setSelected} track={gpsTrack} dieselPrice={DIESEL} events={events} />}
            </div>
          </div>

          {/* secondary grid: event feed + truck rail, below the hero map */}
          <div className="main-grid" style={{ marginTop: 'var(--tg-space-4)' }}>
            <div className="left-col">
              <EventFeed events={events} loading={loading} />
            </div>
            <TruckList trucks={trucks} selected={selected} onSelect={setSelected} loading={loading} />
          </div>
        </div>
      )}

      {selectedTruck && (
        <TruckDetail
          truck={selectedTruck}
          history={selectedHistory}
          ledger={selectedLedger}
          historyLoading={historyLoading}
          events={events.filter(e => e.truck === selectedTruck.name)}
          onClose={() => setSelected(null)}
        />
      )}

      {mobile && (
        <BottomNav
          activeTab={tab}
          onTabChange={setTab}
          alertCount={alertCounts.open}
        />
      )}
    </div>
  )
}
